import React from 'react';
import { useParams } from 'react-router-dom';
import * as z from 'zod';
import { RESOURCE_LIBRARY_ITEM_TYPES } from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import { useLocale } from '../../contexts/LocaleProvider';
import type { LocaleCode } from '../../utils/Locale';
import { getValidLocaleCode } from '../../utils/Locale';
import AssetResourcePage from './AssetResourcePage';
import ArticleResourcePage from './ArticleResourcePage';
import NewsResourcePage from './NewsResourcePage';
import NewProductIntroductionResourcePage from './NewProductIntroductionResourcePage';
import SitePageResourcePage from './SitePageResourcePage';

// Zod schema for the route parameters.
const paramsSchema = z.object({
    itemType: z.enum(RESOURCE_LIBRARY_ITEM_TYPES),
    id: z.string(),
    lang: z.string().optional(),
});

function useValidatedParams() {
    const params = useParams();
    const result = paramsSchema.safeParse(params);
    if (!result.success) {
        throw new Error('Invalid parameters');
    }
    return result.data;
}

export interface ResourcePageContentProps {
    itemType: (typeof RESOURCE_LIBRARY_ITEM_TYPES)[number];
    id: string;
    lang?: LocaleCode;
}

export const ResourcePageContent: React.FC<ResourcePageContentProps> = ({ itemType, id, lang }) => {
    const { locale } = useLocale();
    const effectiveLang = lang ?? locale;

    switch (itemType) {
        case 'asset':
            return <AssetResourcePage id={id} locale={effectiveLang} />;
        case 'article':
            return <ArticleResourcePage id={id} locale={effectiveLang} />;
        case 'news':
            return <NewsResourcePage id={id} locale={effectiveLang} />;
        case 'newProductIntroduction':
            return <NewProductIntroductionResourcePage id={id} locale={effectiveLang} />;
        case 'sitePage':
            return <SitePageResourcePage id={id} locale={effectiveLang} />;
        default:
            return (
                <div className="mid-width">Unsupported resource library item type: {itemType}</div>
            );
    }
};

const ResourcePage: React.FC = () => {
    const { itemType, id, lang } = useValidatedParams();
    const sanitizedLang = getValidLocaleCode(lang);
    return <ResourcePageContent itemType={itemType} id={id} lang={sanitizedLang} />;
};

export default ResourcePage;
